import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { SiteVendor } from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AssignVendorsToSiteModalComponent } from 'src/app/modals/assign-vendors-to-site-modal/assign-vendors-to-site-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-site-vendors',
  templateUrl: './site-vendors.component.html',
  styleUrls: ['./site-vendors.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SiteVendorsComponent implements OnDestroy {
  constructor(
    private SharedDataService: SharedDataService,
    private ModalService: NgbModal,
    private AuthService: AuthService,
    private SiteService: SiteService,
    private ToastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.sharedDataServiceSubscription = this.SharedDataService.listenForEvent<
      number | undefined
    >().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'setSiteId' && typeof data === 'number') {
          this.SiteId = data;

          const vendorQuery = {
            attributes: ['legalName', 'dba'],
            CompanyVendor: {
              attributes: ['id', 'viveStatus', 'viveSubStatus'],
              CompanyId: this.AuthService.getUserCompanyId()
            }
          };

          Object.assign(this.suppliedGridQueryData, {
            attributes: [
              'id',
              'SiteId',
              'ClientRegisteredVendorId',
              'VendorId',
              'ExemptVendorId'
            ],
            SiteId: this.SiteId,
            Vendor: JSON.stringify(vendorQuery),
            ClientRegisteredVendor: JSON.stringify(vendorQuery),
            ExemptVendor: JSON.stringify(vendorQuery)
          });
        }
      }
    });

    this.SharedDataService.sendEvent('getSiteId');
  }

  loggedInUser = this.AuthService.getUser();
  acceptedActionRoles: Set<string> = new Set([
    'ADMIN_ADMIN',
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM',

    'CLIENT_ADMIN'
  ]);
  suppliedGridQueryData: { [key: string]: any } = {};
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['view', 'delete']
  };

  sharedDataServiceSubscription!: Subscription;
  SiteId?: number;
  reloadGridData: boolean = false;
  CompanyId?: number;

  assignVendors() {
    const modalRef = this.ModalService.open(AssignVendorsToSiteModalComponent, {
      centered: true,
      size: 'xl'
    });
    modalRef.componentInstance.SiteId = this.SiteId;
    return modalRef.closed.subscribe({
      next: () => {
        return (this.reloadGridData = true);
      }
    });
  }

  deleteSiteVendor(SiteVendor: SiteVendor) {
    if (!SiteVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Site Vendor found to delete.'
      );
    }

    const modalRef = this.ModalService.open(ConfirmModalComponent, {
      centered: true
    });
    modalRef.componentInstance.displayText =
      'Are you sure you want to remove this Site Vendor?';
    return modalRef.closed.subscribe({
      next: (confirm) => {
        if (confirm) {
          this.SiteService.deleteSiteVendor({
            SiteId: this.SiteId,
            ClientRegisteredVendorId: SiteVendor.ClientRegisteredVendorId,
            ExemptVendorId: SiteVendor.ExemptVendorId,
            VendorId: SiteVendor.VendorId
          }).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully deleted Site Vendor.'
              );
              return (this.reloadGridData = true);
            }
          });
        }

        return;
      }
    });
  }

  onActionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: SiteVendor;
  }) {
    const { id: SiteVendorId, SiteId } = rowData;

    if (action === 'delete') {
      this.deleteSiteVendor(rowData);
    }

    if (action === 'view') {
      this.router.navigate([
        'client',
        this.CompanyId,
        'site',
        SiteId,
        'vendor',
        SiteVendorId,
        'files'
      ]);
    }
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (ParamMap) => {
        const CompanyId = ParamMap.get('CompanyId');

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client id.', {
            returnError: true
          });
        }

        Object.assign(this, { CompanyId });
      }
    });
  }

  ngOnDestroy(): void {
    this.sharedDataServiceSubscription.unsubscribe();
  }
}
